import { useEffect, useState } from 'react'
import Layout from '@/components/Layout'
import { fetchDashboardStats } from '@/lib/api'

interface Stats {
  totalParts: number
  activeParts: number
  featuredParts: number
  totalValue: number
  totalVendors: number
  verifiedVendors: number
  totalWanted: number
  activeWanted: number
  totalZones: number
  totalBrands: number
  totalPosts: number
}

export default function Dashboard() {
  const [stats, setStats] = useState<Stats | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchDashboardStats().then(s => { setStats(s); setLoading(false) }).catch(() => setLoading(false))
  }, [])

  const items = stats ? [
    { label: 'Total Parts', value: stats.totalParts, sub: `${stats.activeParts} active`, color: 'from-indigo-500 to-indigo-600', icon: 'M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z' },
    { label: 'Stock Value (AED)', value: stats.totalValue.toLocaleString(), sub: `${stats.featuredParts} featured`, color: 'from-emerald-500 to-emerald-600', icon: 'M12 2a10 10 0 1010 10A10 10 0 0012 2zm1 5h-2v2h2zm0 12h-2v-7h2z' },
    { label: 'Vendors', value: stats.totalVendors, sub: `${stats.verifiedVendors} verified`, color: 'from-amber-500 to-amber-600', icon: 'M3 21h18M4 3h14M5 3v18m12-18v18M8 7h2m-2 3h2m-2 3h2m4-6h2m-2 3h2m-2 3h2' },
    { label: 'Wanted Orders', value: stats.totalWanted, sub: `${stats.activeWanted} active`, color: 'from-purple-500 to-purple-600', icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' },
    { label: 'Brands', value: stats.totalBrands, sub: 'catalog', color: 'from-sky-500 to-blue-600', icon: 'M4 21V4a1 1 0 011-1h4l2 2h9a1 1 0 011 1v10a1 1 0 01-1 1h-8l-2-2H5' },
    { label: 'Blog Posts', value: stats.totalPosts, sub: 'UAE guides', color: 'from-rose-500 to-pink-600', icon: 'M4 5a2 2 0 012-2h12a2 2 0 012 2v14a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm4 3h8m-8 4h8m-8 4h5' },
  ] : []

  return (
    <Layout title="Dashboard">
      <div className="max-w-7xl mx-auto space-y-7">
        {/* Hero card */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-900 via-slate-800 to-brand-950 p-8 md:p-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-amber-500/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/4" />
          <div className="relative flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-xs font-medium text-amber-200 mb-4 border border-white/5">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                Marketplace live
              </span>
              <h2 className="text-2xl md:text-3xl font-semibold text-white tracking-tight">PartsMarket UAE</h2>
              <p className="text-brand-200/70 mt-2 text-sm max-w-md">Manage parts for sale, vendors, wanted orders and zones — the UAE's used-parts marketplace.</p>
            </div>
            <div className="flex gap-3">
              <a href="/parts/new" className="inline-flex items-center gap-2 bg-amber-400 text-slate-900 text-sm font-semibold px-5 py-2.5 rounded-xl hover:bg-amber-300 transition-colors shadow-lg">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 5v14M5 12h14"/></svg>
                Add Part
              </a>
              <a href="/parts" className="inline-flex items-center gap-2 bg-white/10 text-white text-sm font-medium px-5 py-2.5 rounded-xl hover:bg-white/15 transition-colors border border-white/10">
                View All
              </a>
            </div>
          </div>
        </div>

        {/* Stats grid */}
        {!loading && stats && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {items.map(s => (
              <div key={s.label} className="bg-white rounded-2xl border border-surface-200 shadow-card p-5">
                <div className="flex items-start justify-between mb-4">
                  <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${s.color} flex items-center justify-center shadow-lg`}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d={s.icon}/></svg>
                  </div>
                </div>
                <p className="text-2xl font-bold text-slate-900 tracking-tight">{s.value}</p>
                <div className="flex items-center justify-between mt-1">
                  <p className="text-xs font-medium text-slate-500">{s.label}</p>
                  <span className="text-[10px] font-semibold text-slate-400 bg-surface-100 px-2 py-0.5 rounded-full">{s.sub}</span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Quick actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <a href="/parts" className="group bg-white rounded-2xl border border-surface-200 shadow-card hover:shadow-card-hover transition-all p-6 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-indigo-50 flex items-center justify-center">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#4f46e5" strokeWidth="1.75" strokeLinecap="round"><path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z"/></svg>
            </div>
            <div>
              <p className="font-semibold text-slate-900 text-sm">Parts catalog</p>
              <p className="text-xs text-slate-500 mt-0.5">Add or edit parts for sale</p>
            </div>
            <svg className="ml-auto text-slate-300 group-hover:text-slate-500 transition-colors" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18l6-6-6-6"/></svg>
          </a>
          <a href="/vendors" className="group bg-white rounded-2xl border border-surface-200 shadow-card hover:shadow-card-hover transition-all p-6 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-amber-50 flex items-center justify-center">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="1.75" strokeLinecap="round"><path d="M3 21h18M4 3h14M5 3v18m12-18v18M8 7h2m-2 3h2m-2 3h2m4-6h2m-2 3h2m-2 3h2"/></svg>
            </div>
            <div>
              <p className="font-semibold text-slate-900 text-sm">Vendors</p>
              <p className="text-xs text-slate-500 mt-0.5">Dismantlers and parts companies</p>
            </div>
            <svg className="ml-auto text-slate-300 group-hover:text-slate-500 transition-colors" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18l6-6-6-6"/></svg>
          </a>
          <a href="/wanted" className="group bg-white rounded-2xl border border-surface-200 shadow-card hover:shadow-card-hover transition-all p-6 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="1.75" strokeLinecap="round"><path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
            </div>
            <div>
              <p className="font-semibold text-slate-900 text-sm">Wanted orders</p>
              <p className="text-xs text-slate-500 mt-0.5">Parts requests from workshops</p>
            </div>
            <svg className="ml-auto text-slate-300 group-hover:text-slate-500 transition-colors" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18l6-6-6-6"/></svg>
          </a>
        </div>
      </div>
    </Layout>
  )
}
